import React, { useState } from 'react';
import {
    View,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    Text,
    TextInput,
    Alert,
    Platform,
    ActivityIndicator,
    KeyboardAvoidingView
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, getShadow } from '../../theme/useTheme';
import { postData } from '../../services/api';

const FeedbackScreen = () => {
    const navigation = useNavigation();
    const { colors, spacing, borderRadius } = useTheme();
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState('general');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');

    const feedbackTypes = [
        { id: 'general', label: 'General Feedback', icon: 'chatbox-ellipses' },
        { id: 'suggestion', label: 'Suggestion', icon: 'bulb' },
        { id: 'bug', label: 'Report a Bug', icon: 'bug' },
        { id: 'complaint', label: 'Complaint', icon: 'warning' },
    ];

    const handleSubmit = async () => {
        if (!subject.trim() || !content.trim()) {
            Alert.alert('Error', 'Please fill in both subject and content.');
            return;
        }

        try {
            setLoading(true);
            const result = await postData('feedback', {
                type,
                subject: subject.trim(),
                content: content.trim(),
                metadata: {
                    platform: Platform.OS,
                    version: Platform.Version,
                    submittedAt: new Date().toISOString()
                }
            });

            if (result.success) {
                Alert.alert(
                    'Thank You!',
                    'Your feedback has been submitted successfully. We appreciate your input!',
                    [{ text: 'OK', onPress: () => navigation.goBack() }]
                );
            } else {
                throw new Error(result.error || 'Failed to submit feedback');
            }
        } catch (error) {
            console.error('Feedback submission error:', error);
            Alert.alert('Error', error.response?.data?.error || error.message || 'Failed to submit feedback. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.container}
        >
            <LinearGradient
                colors={[colors.primary.dark, colors.primary.main, colors.primary.light]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.headerBackground}
            />

            <View style={styles.headerContent}>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => navigation.goBack()}
                >
                    <Ionicons name="arrow-back" size={24} color="#FFF" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Send Feedback</Text>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.scrollContent}
            >
                <View style={styles.formCard}>
                    <Text style={styles.sectionLabel}>What kind of feedback do you have?</Text>
                    <View style={styles.typeSelector}>
                        {feedbackTypes.map((item) => (
                            <TouchableOpacity
                                key={item.id}
                                style={[
                                    styles.typeChip,
                                    type === item.id && { backgroundColor: colors.primary.main, borderColor: colors.primary.main }
                                ]}
                                onPress={() => setType(item.id)}
                            >
                                <Ionicons
                                    name={item.icon + (type === item.id ? "" : "-outline")}
                                    size={18}
                                    color={type === item.id ? "#FFF" : colors.text.secondary}
                                />
                                <Text style={[
                                    styles.typeChipText,
                                    type === item.id && styles.typeChipTextActive
                                ]}>
                                    {item.label}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.inputLabel}>Subject</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="Brief summary..."
                            value={subject}
                            onChangeText={setSubject}
                            placeholderTextColor="#94A3B8"
                        />
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.inputLabel}>Details</Text>
                        <TextInput
                            style={[styles.input, styles.textArea]}
                            placeholder="Tell us more about your feedback..."
                            value={content}
                            onChangeText={setContent}
                            multiline
                            numberOfLines={6}
                            textAlignVertical="top"
                            placeholderTextColor="#94A3B8"
                        />
                    </View>

                    <TouchableOpacity
                        style={styles.submitButton}
                        onPress={handleSubmit}
                        disabled={loading}
                    >
                        <LinearGradient
                            colors={[colors.primary.main, colors.primary.dark]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={[styles.submitButtonGradient, loading && { opacity: 0.7 }]}
                        >
                            {loading ? (
                                <ActivityIndicator color="#FFF" size="small" />
                            ) : (
                                <>
                                    <Ionicons name="send" size={18} color="#FFF" style={{ marginRight: 8 }} />
                                    <Text style={styles.submitButtonText}>Submit Feedback</Text>
                                </>
                            )}
                        </LinearGradient>
                    </TouchableOpacity>
                </View>

                <View style={styles.infoBox}>
                    <Ionicons name="information-circle-outline" size={20} color={colors.primary.main} />
                    <Text style={styles.infoText}>
                        Your feedback helps us improve the Youth Connect experience for everyone.
                    </Text>
                </View>
            </ScrollView>
        </KeyboardAvoidingView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#EEF2FF',
    },
    headerBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: Platform.OS === 'ios' ? 140 : 120,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: Platform.OS === 'ios' ? 50 : 35,
        paddingHorizontal: 18,
        zIndex: 10,
    },
    backButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: 'rgba(255,255,255,0.2)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFF',
        marginLeft: 12,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingTop: 20,
        paddingBottom: 40,
    },
    formCard: {
        backgroundColor: '#FFF',
        marginHorizontal: 16,
        borderRadius: 20,
        padding: 20,
        ...getShadow('lg'),
    },
    sectionLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
        marginBottom: 15,
    },
    typeSelector: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 20,
    },
    typeChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    typeChipText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#64748B',
        marginLeft: 6,
    },
    typeChipTextActive: {
        color: '#FFF',
    },
    inputGroup: {
        marginBottom: 18,
    },
    inputLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: '#475569',
        marginBottom: 8,
        marginLeft: 4,
    },
    input: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 15,
        color: '#1E293B',
    },
    textArea: {
        height: 120,
        paddingTop: 12,
    },
    submitButton: {
        marginTop: 10,
        borderRadius: 12,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    submitButtonGradient: {
        flexDirection: 'row',
        paddingVertical: 14,
        justifyContent: 'center',
        alignItems: 'center',
    },
    submitButtonText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '700',
    },
    infoBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        marginHorizontal: 16,
        marginTop: 20,
        padding: 15,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: 'rgba(99, 102, 241, 0.2)',
    },
    infoText: {
        flex: 1,
        fontSize: 13,
        color: '#4F46E5',
        marginLeft: 10,
        lineHeight: 18,
    }
});

export default FeedbackScreen;
